import { h, Fragment } from "preact";
import { RoutableProps } from "preact-router";
import { useEffect, useState } from "preact/hooks";
import { generate } from "../../api/label-creator";
import {
    addLabelToProductTypeRevision,
    deleteProductTypeRevision,
    getGroupLabels,
    getLabels,
    getProductTypeRevision,
    Label,
    ProductTypeRevision,
    removeLabelFromProductTypeRevision,
} from "../../api/product-api";
import { route } from "../../App";
import { CompactTableItemSelector } from "../../components/CompactTableItemSelector";
import { Snackbar, SnackbarShowCommand } from "../../components/Snackbar";
import { useCommand } from "../../hooks";
import { generateExampleFieldsForLabel, generateLabelFields, parseToDateAndFormatIfExists } from "../../utils";
import "./styles.scss";

export function ProductTypeRevisionPage(props: RoutableProps & { id?: string }) {
    const [productTypeRevision, setProductTypeRevision] = useState(null as null | ProductTypeRevision);
    const [ordersSearch, setOrdersSearch] = useState("");
    const [refresh, setRefresh] = useState(0);
    const [page, setPage] = useState(0);
    const [count, setCount] = useState(100);
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const templateMetadataDisplayClear = useCommand();
    const templateMetadataDisplaySetMetadata = useCommand<[metadata: { [key: string]: string }]>();

    useEffect(() => {
        if (props.id) {
            getProductTypeRevision(+props.id).then(async label => {
                setProductTypeRevision(label);
            });
        } else {
            setProductTypeRevision(null);
        }
    }, [page, count, refresh]);

    if (!productTypeRevision) {
        return (
            <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
                <div>Pobieranie danych...</div>
                <div class="loader"></div>
            </div>
        );
    }

    const canRemove =
        !productTypeRevision.products?.length &&
        !productTypeRevision.productTypeRevisionLabels?.length &&
        productTypeRevision.productType?.currentProductTypeRevision?.id !== productTypeRevision.id;

    return (
        <div class="product-list-page">
            <Snackbar doShow={showSnackbarCommand} />
            <div class="order-items paper flex flex-row" style="margin-top: 10px;">
                <div class="grow">
                    <h1>
                        Rewizja: {productTypeRevision.productType!.name}-{productTypeRevision.modelName}
                    </h1>
                    <div style="margin: 10px 0px;">
                        <button onClick={() => route("/product-type/" + productTypeRevision.productType!.id)}>
                            Przejdź do rodzaju produktu
                        </button>
                    </div>
                    <div>Wersja: {productTypeRevision.name}</div>
                    <div>Wersja (do modelu): {productTypeRevision.modelName}</div>
                    <div>GTIN: {productTypeRevision.gtin}</div>
                    <div>Rejestrujący: {productTypeRevision.creator.email}</div>
                    <div>Data rejestracji: {parseToDateAndFormatIfExists(productTypeRevision.createdAt)}</div>
                    <div>Ilość produktów tej rewizji: {productTypeRevision.products?.length}</div>
                </div>
                <div>
                    <div
                        class={"self-start tooltip-left" + (!canRemove ? " tooltip" : "")}
                        data-tooltip={
                            !canRemove
                                ? "Usunięcie rewizji produktu jest możliwe tylko wtedy, gdy nie ma żadnych produktów lub naklejek przypisanych do tej rewizji."
                                : ""
                        }
                    >
                        <button
                            type="button"
                            class={"error" + (!canRemove ? " disabled" : "")}
                            onClick={() => {
                                deleteProductTypeRevision(productTypeRevision.id)
                                    .then(() => route("/product-type/" + productTypeRevision.productType!.id))
                                    .catch(() =>
                                        showSnackbarCommand.run(
                                            "Nie udało się usunąć rewizji produktu",
                                            "error",
                                            "error",
                                            2000,
                                        ),
                                    );
                            }}
                        >
                            Usuń
                        </button>
                    </div>
                </div>
            </div>

            <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
                <h1>Dodawanie naklejki</h1>
                <AddLabel
                    productTypeRevision={productTypeRevision}
                    onCommit={() => {
                        setRefresh(refresh + 1);
                    }}
                />
            </div>

            <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
                <h1>Naklejki przypisane do rewizji</h1>
                <div>
                    {productTypeRevision.productTypeRevisionLabels!.map(v => (
                        <div style="margin-top: 10px;">
                            <table class="list selectable">
                                <thead>
                                    <tr>
                                        <th>Typ</th>
                                        <th>Nazwa</th>
                                        <th>{v.label.type === "normal" ? "Ścieżka" : "Naklejki"}</th>
                                        <th>Rewizja</th>
                                        <th>Rejestrujący</th>
                                        <th>Data rejestracji</th>
                                        <th>Akcje</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        class="item"
                                        onClick={() =>
                                            v.label.type === "normal"
                                                ? route("/label/" + v.label.id)
                                                : route("/label/group/" + v.label.id)
                                        }
                                    >
                                        <td>{v.label.type === "normal" ? "Pojedyncza" : "Wieloczęściowa"}</td>
                                        <td>{v.label.name}</td>
                                        <td>
                                            {v.label.type === "normal" ? (
                                                v.label.path
                                            ) : (
                                                <>
                                                    {v.label.innerLabels?.slice(0, 5).map(v => (
                                                        <div>{v.label.name}</div>
                                                    ))}
                                                    {(v.label.innerLabels?.length ?? 0) > 5 ? (
                                                        <div class="text-gray-400 select-none">
                                                            ... więcej naklejek ...
                                                        </div>
                                                    ) : null}
                                                </>
                                            )}
                                        </td>
                                        <td>{v.label.revision}</td>
                                        <td>{v.label.creator.email}</td>
                                        <td>{parseToDateAndFormatIfExists(v.label.createdAt)}</td>
                                        <td class="text-center">
                                            <button
                                                class="error"
                                                onClick={async e => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    await removeLabelFromProductTypeRevision(
                                                        v.label,
                                                        v.productTypeRevision,
                                                    );
                                                    setRefresh(refresh + 1);
                                                }}
                                            >
                                                Usuń
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style="margin-top: 10px;">
                                <img
                                    src={generate(
                                        v.label.path,
                                        {
                                            ...generateExampleFieldsForLabel(v.label.metadata?.metadata ?? {}),
                                            ...generateLabelFields({ revision: productTypeRevision }),
                                        },
                                        v.label.metadata?.metadata,
                                    )}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function AddLabel(props: { productTypeRevision: ProductTypeRevision; onCommit: () => void }) {
    const [labels, setLabels] = useState([] as Label[]);
    const [filteredLabels, setFilteredLabels] = useState([] as Label[]);
    const [labelsSearch, setLabelsSearch] = useState("");
    const [selectedLabel, setSelectedLabel] = useState(null as Label | null);

    useEffect(() => {
        getLabels().then(async labels => {
            const groups = await getGroupLabels();
            setLabels(labels.concat(groups.filter(v => v.metadata?.metadata?.groupType === "multipart")));
        });
    }, []);

    useEffect(() => {
        if (labels && labelsSearch) {
            const filtered = labels
                .filter(
                    item =>
                        !labelsSearch ||
                        item.name.toLowerCase().indexOf(labelsSearch.toLowerCase()) >= 0 ||
                        item.path.toLowerCase().indexOf(labelsSearch.toLowerCase()) >= 0 ||
                        item.creator.email.toLowerCase().indexOf(labelsSearch.toLowerCase()) >= 0 ||
                        parseToDateAndFormatIfExists(item.createdAt).toLocaleString().indexOf(labelsSearch) >= 0,
                )
                .slice(0, 10);

            if (selectedLabel && filtered.indexOf(selectedLabel) < 0) {
                filtered.unshift(selectedLabel);
            }

            setFilteredLabels(filtered);
        } else {
            setFilteredLabels(selectedLabel ? [selectedLabel] : []);
        }
    }, [labels, labelsSearch, selectedLabel]);

    return (
        <div>
            <CompactTableItemSelector
                items={labels}
                properties={[
                    {
                        name: "type",
                        type: "text",
                        title: "Typ",
                        search: true,
                        format: value => (value === "normal" ? "Pojedyncza" : "Wieloczęściowa"),
                        filter: ["Pojedyncza", "Wieloczęściowa"],
                    },
                    { name: "name", type: "text", title: "Nazwa", search: true },
                    { name: "path", type: "text", title: "Ścieżka", search: true },
                    { name: "revision", type: "text", title: "Rewizja", search: true },
                    { name: "creator.email", type: "text", title: "Rejestrujący", search: true },
                    { name: "createdAt", type: "date", title: "Data rejestracji", search: true },
                ]}
                onSelect={v => {
                    setSelectedLabel(v);
                }}
            />
            <div>
                <div style="margin-top: 10px;">
                    <button
                        type="button"
                        class={selectedLabel ? "" : "disabled"}
                        onClick={async () => {
                            if (selectedLabel) {
                                await addLabelToProductTypeRevision(selectedLabel, props.productTypeRevision);
                                props.onCommit();
                            }
                        }}
                    >
                        Dodaj naklejkę do rewizji
                    </button>
                </div>
            </div>
        </div>
    );
}
